import React from 'react';
import { Calendar } from 'lucide-react';

const MONTHS: { value: string; label: string }[] = [
    { value: '01', label: 'Jan' }, { value: '02', label: 'Fev' }, { value: '03', label: 'Mar' },
    { value: '04', label: 'Abr' }, { value: '05', label: 'Mai' }, { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' }, { value: '08', label: 'Ago' }, { value: '09', label: 'Set' },
    { value: '10', label: 'Out' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dez' },
];

/** Value is YYYY-MM. */
interface PeriodInputProps {
    value: string;
    onChange: (yyyyMm: string) => void;
    name?: string;
    label?: string;
    disabled?: boolean;
    /** Compact: single row with label inline, for filter bars */
    compact?: boolean;
}

/** Converts YYYY-MM to first day of month UTC ISO. */
export function periodToStartIso(yyyyMm: string): string {
    const [y, m] = yyyyMm.split('-').map(Number);
    if (!y || !m || m < 1 || m > 12) return new Date().toISOString();
    return new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0)).toISOString();
}

/** Converts YYYY-MM to last day of month UTC ISO. */
export function periodToEndIso(yyyyMm: string): string {
    const [y, m] = yyyyMm.split('-').map(Number);
    if (!y || !m || m < 1 || m > 12) return new Date().toISOString();
    return new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)).toISOString();
}

/** Converts an ISO date string to YYYY-MM. */
export function isoToPeriod(iso: string | null): string {
    if (!iso) {
        const now = new Date();
        const y = now.getUTCFullYear();
        const m = String(now.getUTCMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
    }
    const d = new Date(iso);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => currentYear - 3 + i);

const PeriodInput: React.FC<PeriodInputProps> = ({ value, onChange, name, label, disabled, compact }) => {
    const [y, m] = value && value.length >= 7 ? [value.slice(0, 4), value.slice(5, 7)] : [
        String(currentYear),
        String(new Date().getMonth() + 1).padStart(2, '0'),
    ];

    const handleMonthChange = (month: string) => {
        onChange(`${y}-${month}`);
    };
    const handleYearChange = (year: string) => {
        onChange(`${year}-${m}`);
    };

    const selectClass = 'rounded-lg border border-secondary-300 bg-white px-2.5 py-2 text-secondary-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-0';

    const content = (
        <div className={`flex items-center gap-1.5 ${compact ? 'flex-1' : ''}`}>
            <select
                id={name ? `${name}-month` : undefined}
                name={name ? `${name}-month` : undefined}
                value={m}
                onChange={(e) => handleMonthChange(e.target.value)}
                disabled={disabled}
                className={`${selectClass} max-w-[5.5rem]`}
                aria-label="Mês"
            >
                {MONTHS.map((mo) => (
                    <option key={mo.value} value={mo.value}>{mo.label}</option>
                ))}
            </select>
            <span className="text-secondary-400 font-medium shrink-0">/</span>
            <select
                id={name ? `${name}-year` : undefined}
                name={name ? `${name}-year` : undefined}
                value={y}
                onChange={(e) => handleYearChange(e.target.value)}
                disabled={disabled}
                className={`${selectClass} max-w-[5rem]`}
                aria-label="Ano"
            >
                {YEAR_OPTIONS.map((yr) => (
                    <option key={yr} value={yr}>{yr}</option>
                ))}
            </select>
            {!compact && (
                <Calendar className="h-4 w-4 text-secondary-400 shrink-0" aria-hidden />
            )}
        </div>
    );

    if (compact) {
        return (
            <div className="flex flex-col gap-1">
                {label && (
                    <label htmlFor={name ? `${name}-month` : undefined} className="block text-sm font-medium text-secondary-700">
                        {label}
                    </label>
                )}
                <div className="rounded-lg border border-secondary-300 bg-white px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 inline-flex">
                    {content}
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            {label && (
                <label htmlFor={name ? `${name}-month` : undefined} className="block text-sm font-medium text-secondary-700 mb-1">
                    {label}
                </label>
            )}
            <div className="flex items-center gap-2 rounded-lg border border-secondary-300 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
                {content}
            </div>
        </div>
    );
};

export default PeriodInput;
